/**
 * Gate abbonamento — punto UNICO di intercettazione del 403 "abbonamento scaduto".
 *
 * Vive fuori da api.ts per una ragione sola: così è verificabile senza montare
 * axios. L'interceptor lo chiama e basta.
 *
 * Nota di realtà (09/09/2026): oggi nessun endpoint del backend Thermo emette
 * questo codice — i 403 di Thermo sono `HTTPException(403, "stringa")` e quindi
 * arrivano come `{"detail": "..."}`. Questa è la presa, non l'enforcement: il
 * giorno che il backend spegne un tenant scaduto deve rispondere
 * `403 {"detail": {"code": "SUBSCRIPTION_EXPIRED", ...}}` e l'utente atterra
 * dove deve. Fino ad allora la pagina si raggiunge solo per rotta diretta.
 */

export const SUBSCRIPTION_EXPIRED_CODE = 'SUBSCRIPTION_EXPIRED'
export const SUBSCRIPTION_EXPIRED_PATH = '/subscription-expired'

/**
 * Le vie d'uscita non devono MAI reindirizzare: sono le uniche chiamate che un
 * tenant bloccato deve poter fare (pagare, restare autenticato).
 */
const ESCAPE_HATCHES = ['/api/billing', '/api/auth']

interface GateErrorLike {
  config?: { url?: string }
  response?: { status?: number; data?: unknown }
}

/**
 * Estrae il codice applicativo da un corpo d'errore, tollerando le due forme:
 *  - `{"detail": {"code": "..."}}`  (FastAPI con detail strutturato)
 *  - `{"code": "..."}`              (forma piatta)
 * Su `{"detail": "stringa"}` NON inventa niente: torna null.
 */
export function extractErrorCode(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const body = payload as Record<string, unknown>

  const detail = body.detail
  if (detail && typeof detail === 'object') {
    const code = (detail as Record<string, unknown>).code
    if (typeof code === 'string') return code
  }

  if (typeof body.code === 'string') return body.code
  return null
}

/**
 * Porta l'utente alla pagina di blocco se — e solo se — l'errore è un 403 con
 * codice SUBSCRIPTION_EXPIRED. Torna true se ha reindirizzato.
 */
export function handleSubscriptionGate(error: unknown): boolean {
  const err = (error ?? {}) as GateErrorLike
  if (err.response?.status !== 403) return false
  if (extractErrorCode(err.response?.data) !== SUBSCRIPTION_EXPIRED_CODE) return false

  const url = err.config?.url || ''
  if (ESCAPE_HATCHES.some((prefix) => url.startsWith(prefix))) return false

  if (typeof window === 'undefined') return false
  if (window.location.pathname.startsWith(SUBSCRIPTION_EXPIRED_PATH)) return false

  window.location.assign(SUBSCRIPTION_EXPIRED_PATH)
  return true
}
